// React
import React, { useMemo, useEffect, useState, useRef } from "react";
import { createRoot } from "react-dom/client";

// Libraries
import PropTypes from "prop-types";
import { isEmpty, get, reduce, orderBy, map, flatten, filter } from "lodash";
import moment from "moment";

// Redux
import { Provider, connect } from "react-redux";
import store from "@Redux";
import { getChatRemainingMessages, removeReply } from "@Actions/chats";

// Utils
import { dateSort } from "@Utils";

// Context
import { ListRefProvider, useListRef } from "@Features/Monitor/Context";

// Components
import EmptyList from "@Components/EmptyList";
import PrintRender from "@Components/PrintRender";
import ChatHeader from "../ChatHeader";
import MessageList from "../MessageList";
import MessageListPrint from "../MenssageListPrint";
import ReplyBar from "../ReplyBar";
import ChatInputBar from "../ChatInputBar";
import MessageCard from "../MessageCard";
import useAlert from "@Hooks/useAlert";

// Modals
import Share from "../../Modals/Share";

// Styles
import styles from "./styles.module.css";

moment.locale("es");

const ChatArea = ({
	dispatch,
	isHistoric = false,
	alerts,
	currentChat,
	reply,
	companyName,
	userName,
	isViewAutogestion,
	isChatDisabled = false,
	isWhatsappBlock = false,
}) => {
	const [isLoading, setIsLoading] = useState(false);
	const [shareMessage, setShareMessage] = useState({});
	const [isShareModalOpen, setIsShareModalOpen] = useState(false);
	const [command, setCommand] = useState("");
	const [cmdSearch, setCmdSearch] = useState("");
	const [clearInputTrigger, setClearInputTrigger] = useState(0);
	const [printAction, setPrintAction] = useState(false);
	const printRef = useRef(null);
	const initialFetchChatIdRef = useRef(null);
	const { listRef } = useListRef();
	const clientName = get(currentChat, "contact.name", "");
	const clientPhone = get(currentChat, "contact.phone", "");
	const clientEmail = get(currentChat, "contact.email", "");
	const platformName = get(currentChat, "channel.label", "");
	const platformTypes = get(currentChat, "channel.platform.supportedTypes", []);
	const tags = get(currentChat, "tags", []);
	const groupName = get(currentChat, "group.name", "");
	const filteredTags = tags.length > 0 ? tags : [];
	const createdDate = get(currentChat, "createdDate", 0);
	const state = get(currentChat, "state", "Atender");
	const alert = useAlert(currentChat, alerts, isHistoric);
	const alertName = alert?.name;
	const alertColor = alert?.color;

	const [sectionsData, messagesData, dataCounts] = useMemo(() => {
		const filteredMessages = isViewAutogestion
			? currentChat?.messages
			: filter(currentChat?.messages, (item) => {
					return isEmpty(item.autogestion);
			  });

		const groups = reduce(
			orderBy(filteredMessages, "timestamp", "desc"),
			(acc, item) => {
				const date = item.createdDate.split("T")[0];
				const updated = { ...acc };
				if (!updated[date]) updated[date] = [];
				updated[date].unshift(item);
				return updated;
			},
			{}
		);

		const groupArrays = Object.keys(groups)
			.sort(dateSort)
			.map((date) => {
				return { title: moment(date).format("MMMM D, YYYY"), items: groups[date] };
			});

		const groupCounts = map(groupArrays, (group) => {
			return group.items.length;
		});
		const sections = map(groupArrays, (group) => {
			return group.title;
		});
		const messages = flatten(
			map(groupArrays, (group) => {
				return group.items;
			})
		);
		return [sections, messages, groupCounts];
	}, [
		currentChat?.id,
		currentChat?.channelId,
		currentChat?.contactId,
		currentChat?.count,
		currentChat?.newChat,
		currentChat?.state,
		currentChat?.tagId,
		currentChat?.userId,
		currentChat?.messages?.length,
		currentChat?.message?.id,
		currentChat?.message?.status,
		currentChat?.message?.call?.status,
		currentChat?.message?.call?.duration,
		isViewAutogestion,
	]);

	const startingMessageId = useMemo(() => {
		if (isEmpty(currentChat?.messages)) return null;
		return reduce(currentChat.messages, (min, curr) => {
			return min.id < curr.id ? min : curr;
		})?.id;
	}, [currentChat?.id, currentChat?.messages?.length]);

	useEffect(() => {
		if (isEmpty(currentChat) || currentChat.hasFetched) {
			return;
		}
		if (initialFetchChatIdRef.current === currentChat.id) {
			return;
		}
		initialFetchChatIdRef.current = currentChat.id;
		setIsLoading(true);
		dispatch(
			getChatRemainingMessages({
				channelCid: currentChat?.channelCid,
				chatId: currentChat?.id,
				offset: currentChat?.messages?.length ?? 0,
				isLoading: setIsLoading,
				isHistoric,
				startingMessageId,
			})
		);
	}, [currentChat?.id, currentChat?.hasFetched]);

	const fetchMoreMessages = async (callback, messageId = null) => {
		if (!currentChat?.endFetching) {
			setIsLoading(true);
			const newMessages = await dispatch(
				getChatRemainingMessages({
					channelCid: currentChat?.channelCid,
					chatId: currentChat?.id,
					offset: currentChat?.messages?.length ?? 0,
					isLoading: setIsLoading,
					isHistoric,
					messageId,
					startingMessageId,
				})
			);

			// Measure total height of new items
			const tempContainer = document.createElement("div");
			tempContainer.style.position = "absolute";
			tempContainer.style.left = "-9999px";
			document.body.appendChild(tempContainer);

			const root = createRoot(tempContainer);

			const renderedItems = newMessages.map((item, index) => {
				return <MessageCard key={index} item={item} />;
			});

			root.render(
				<Provider store={store}>
					<ListRefProvider>
						<div>{renderedItems}</div>
					</ListRefProvider>
				</Provider>
			);
			await new Promise((resolve) => {
				return setTimeout(resolve, 50);
			});
			const newItemsHeight = Array.from(tempContainer.children).reduce((total, child) => {
				return total + child.getBoundingClientRect().height;
			}, 0);

			// Clean up
			root.unmount();
			document.body.removeChild(tempContainer);
			return callback(newItemsHeight, newMessages);
		}
		return callback(0);
	};

	const handleRemoveReply = () => {
		dispatch(removeReply());
	};

	const handleShare = (item) => {
		setShareMessage(item);
		setIsShareModalOpen(true);
	};

	const handleClose = () => {
		setShareMessage({});
		setIsShareModalOpen(false);
	};

	if (isEmpty(currentChat)) {
		return (
			<div className={styles.container}>
				<EmptyList icon="comment alternate outline" text="No Haz Seleccionado Una Conversación" />
			</div>
		);
	}

	return (
		<div className={styles.container}>
			{/* Modal de Reenvio () */}
			{isShareModalOpen && <Share shareMessage={shareMessage} onClose={handleClose} />}

			<ChatHeader
				currentChat={currentChat}
				handlePrint={() => {
					return setPrintAction(true);
				}}
				isDisabled={isChatDisabled}
				isWhatsappBlock={isWhatsappBlock}
				isHistoric={isHistoric}
				command={command}
				setCommand={setCommand}
				cmdSearch={cmdSearch}
				setCmdSearch={setCmdSearch}
				onSuccessSend={() => {
					return setClearInputTrigger((prev) => {
						return prev + 1;
					});
				}}
			/>
			<div ref={printRef} className={`${styles.messagesContainer} ${styles.regular}`}>
				{printAction ? (
					<MessageListPrint sectionsData={sectionsData} messagesData={messagesData} dataCounts={dataCounts} platformName={platformName} />
				) : (
					<MessageList
						key={currentChat?.id}
						ref={listRef}
						sectionsData={sectionsData}
						messagesData={messagesData}
						dataCounts={dataCounts}
						platformTypes={platformTypes}
						fetchMoreMessages={fetchMoreMessages}
						isLoading={isLoading}
						isChatDisabled={isChatDisabled}
						handleShare={handleShare}
						clientName={clientName}
						clientPhone={clientPhone}
						platformName={platformName}
					/>
				)}
			</div>

			{!isEmpty(reply) && <ReplyBar item={reply} removeReply={handleRemoveReply} />}
			<ChatInputBar
				currentChat={currentChat}
				isDisabled={isChatDisabled}
				setCommand={setCommand}
				setCmdSearch={setCmdSearch}
				clearInputTrigger={clearInputTrigger}
				isWhatsappBlock={isWhatsappBlock}
				platformTypes={platformTypes}
			/>

			<PrintRender
				hiddenButon
				printAction={printAction}
				printActionRetry={() => {
					return setPrintAction(false);
				}}
				printHeader={{ company: companyName, user: userName, title: "Conversación" }}
				alertName={alertName}
				alertColor={alertColor}
				state={state}
				clientName={clientName}
				clientPhone={clientPhone}
				clientEmail={clientEmail}
				platform={platformName}
				tags={filteredTags}
				group={groupName}
				renderData={printRef}
				handleBeforeGetContent
				createdDate={createdDate}
			/>
		</div>
	);
};

ChatArea.propTypes = {
	dispatch: PropTypes.func,
	isHistoric: PropTypes.bool,
	alerts: PropTypes.array,
	currentChat: PropTypes.object,
	reply: PropTypes.object,
	companyName: PropTypes.string,
	userName: PropTypes.string,
	isChatDisabled: PropTypes.bool,
	isWhatsappBlock: PropTypes.bool,
	isViewAutogestion: PropTypes.bool,
};

function mapStateToProps({ user, company, chats }) {
	return {
		companyName: company.name,
		alerts: company.settings.alerts,
		userName: user.name,
		reply: chats.reply,
		isViewAutogestion: chats.isViewAutogestion,
	};
}

export default connect(mapStateToProps)(ChatArea);
